import React, { useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormDialog, RhfColorPicker, RhfTextField } from '@core/components';
import type { Color, CreateColorData } from '../../api/color';
import { createColor, updateColor } from '../../api/color';
import { suggestHexFromName } from '../utils/color';

interface ColorDialogProps {
  open: boolean;
  onClose: () => void;
  color?: Color | null;
  onSuccess?: () => void;
}

type ColorFormValues = {
  name: string;
  hexCode: string;
};

const ColorDialog: React.FC<ColorDialogProps> = ({
  open,
  onClose,
  color,
  onSuccess,
}) => {
  const defaultValues = useMemo<ColorFormValues>(
    () => ({
      name: color?.name ?? '',
      hexCode: color?.hexCode ?? '',
    }),
    [color]
  );

  const onSubmit = async (data: ColorFormValues) => {
    const payload: CreateColorData = {
      name: data.name.trim(),
      hexCode: data.hexCode.trim(),
    };
    if (color) {
      await updateColor(color.id, payload);
      toast.success('Color updated successfully');
    } else {
      await createColor(payload);
      toast.success('Color created successfully');
    }
    onSuccess?.();
  };

  return (
    <FormDialog<ColorFormValues>
      open={open}
      onClose={onClose}
      title={color ? 'Edit Color' : 'Add Color'}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={color ? 'Update' : 'Add'}
      cancelText="Cancel"
    >
      {(methods) => (
        <>
          <RhfTextField<ColorFormValues>
            name="name"
            label="Name"
            required
            textFieldProps={{
              onBlur: () => {
                if (methods.getValues('hexCode')?.trim()) return;
                const suggestedHexCode = suggestHexFromName(
                  methods.getValues('name')
                );
                if (!suggestedHexCode) return;
                methods.setValue('hexCode', suggestedHexCode, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              },
            }}
          />
          <RhfColorPicker<ColorFormValues>
            name="hexCode"
            label="Hex code"
            required
          />
        </>
      )}
    </FormDialog>
  );
};

export default ColorDialog;
